import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import requestsClient from "../../api/private/requestsClient";
import { getTableQueryKey } from "../../helpers/tableHelpers";
import { CustomerPortalRequestTableQuery } from "./models";
import {
  CPRequestDetail,
  CPRequestFormDataForOwnerResult,
  CPRequestMessageToAdd,
  CPRequestStateToChangeInput,
  CPRequestToAdd,
  TableResultCPRequest,
} from "../../api/models/unified";

const requestsKey = "requestsKey";
const requestDetailKey = "requestDetailKey";
const requestCustomerDataKey = "requestCustomerDataKey";

export const useGetRequestsQuery = (
  params: CustomerPortalRequestTableQuery,
) => {
  return useQuery<TableResultCPRequest>({
    queryKey: getTableQueryKey(requestsKey, params),
    queryFn: () => requestsClient.getRequests(params),
  });
};

export const useAddRequestMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<CPRequestDetail, string, CPRequestToAdd>({
    mutationFn: (variables) => requestsClient.addRequest(variables),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [requestsKey] }),
  });
};

export const useGetRequestDetailQuery = (requestId?: string) => {
  return useQuery<CPRequestDetail>({
    queryKey: [requestDetailKey, requestId],
    queryFn: () => requestsClient.getRequestDetail(requestId ?? ""),
    enabled: !!requestId,
  });
};

export const useAddRequestMessageMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    CPRequestDetail,
    string,
    { requestId: string; values: CPRequestMessageToAdd }
  >({
    mutationFn: (variables) => requestsClient.addMessage(variables),
    onSuccess: (data, { requestId }) =>
      queryClient.setQueryData([requestDetailKey, requestId], data),
  });
};

export const useChangeRequestStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    CPRequestDetail,
    string,
    { requestId: string; values: CPRequestStateToChangeInput }
  >({
    mutationFn: (variables) => requestsClient.changeState(variables),
    onSuccess: (data, { requestId }) =>
      queryClient.setQueryData([requestDetailKey, requestId], data),
  });
};

export const useGetCustomerDataQuery = (customerId?: string) => {
  return useQuery<CPRequestFormDataForOwnerResult>({
    queryKey: [requestCustomerDataKey, customerId],
    queryFn: () => requestsClient.getCustomerData(customerId ?? ""),
    enabled: !!customerId,
  });
};
